import { useAppDispatch } from '../../../redux/hooks';
import { SecondaryButton } from '../../Button';
import { assertCondition } from '../../../utils/types';
import { DASHBOARD_REPLACE_ANALYSIS_MUTATION } from '../../../utils/apollo/dashboards';
import { WORKSPACE_REPLACE_ANALYSES } from '../../../utils/apollo/workspaces';
import { useMutation } from '@apollo/client';
import {
  AnalysisStatus,
  IDashboardReplaceAnalysisMutation,
  IDashboardReplaceAnalysisMutationVariables,
  IWorkspaceReplaceAnalysesMutation,
  IWorkspaceReplaceAnalysesMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { appRoutes } from '../../../path';
import toastMessage from '../../../utils/toastMessage';
import {
  clearReplaceReport,
  IReplaceReport,
} from '../../../redux/slices/cachedEntities';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { dashboardUpdated } from '../../../utils/analytics/events/dashboard/events';
import { reportReplaced } from '../../../utils/analytics/events/report/events';
import { workspaceUpdated } from '../../../utils/analytics/events/workspace/events';
import { usePrepareChartPrefs } from '../hooks/useAnalysisSetChartPref';
import { UnreachableCaseError } from 'ts-essentials';
import KubitTooltip from '../../KubitTooltip';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
  replaceReport: IReplaceReport;
}

export const getReplaceReportContainerName = (
  replaceReport: IReplaceReport,
) => {
  switch (replaceReport.type) {
    case 'dashboard':
      return `dashboard ${replaceReport.name}`;
    case 'workspace':
      return `workspace ${replaceReport.name}`;
    default:
      throw new UnreachableCaseError(replaceReport);
  }
};

/**
 * This component is for the feature KB-4338,
 * It will allow user to replace the new executed analysis in Workspace/Dashboard.
 */
const ReplaceReportAction = ({ analysisId, replaceReport }: IProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  const { metadata } = useChartContext();
  const { display, description } = metadata;
  const { type, targetAnalysisId } = replaceReport;

  const preparePrefInput = usePrepareChartPrefs(analysisId);

  const handleReplacedDashboardReport = ({
    dashboardReplaceAnalysis,
  }: IDashboardReplaceAnalysisMutation) => {
    const { entity, errors, warningMessage } = dashboardReplaceAnalysis;

    if (printGraphqlErrors(errors)) {
      return;
    }

    // If warningMessage is not empty, it means that
    // Dashboard was updated by another user.
    if (warningMessage) {
      toastMessage({
        message: warningMessage,
        type: 'error',
      });
    }

    const newDashboardBaseId = entity?.baseId;

    if (newDashboardBaseId) {
      track(dashboardUpdated(entity));
      track(reportReplaced(metadata));
      // Show toast message with action
      toastMessage({
        message: 'Report has been replaced on Dashboard.',
        type: 'action',
        buttonLabel: 'View Dashboard',
        redirectPath: appRoutes.dashboard.generateLink(newDashboardBaseId),
      });
    }

    dispatch(clearReplaceReport());
  };
  const [replaceAnalysisInDashboard, { loading: replacingDashboardReport }] =
    useMutation<
      IDashboardReplaceAnalysisMutation,
      IDashboardReplaceAnalysisMutationVariables
    >(DASHBOARD_REPLACE_ANALYSIS_MUTATION, {
      onCompleted: handleReplacedDashboardReport,
    });

  const handleReplacedWorkspaceReport = ({
    wsReplaceAnalysis,
  }: IWorkspaceReplaceAnalysesMutation) => {
    const { entity, errors, newAnalysisId, warningMessage } = wsReplaceAnalysis;

    if (printGraphqlErrors(errors)) {
      return;
    }

    // If warningMessage is not empty, it means that
    // Workspace was updated by another user.
    if (warningMessage) {
      toastMessage({
        message: warningMessage,
        type: 'error',
      });
    }

    const workspaceId = entity?.id;

    if (workspaceId && newAnalysisId) {
      track(workspaceUpdated(entity));
      track(reportReplaced(metadata));

      // Show a toast message with action
      toastMessage({
        message: 'Report has been replaced in Workspace.',
        type: 'action',
        buttonLabel: 'View Workspace',
        redirectPath: appRoutes.workspaces.workspace(workspaceId),
      });
    }
    dispatch(clearReplaceReport());
  };

  const [replaceAnalysisInWorkspace, { loading: replacingWorkspaceReport }] =
    useMutation<
      IWorkspaceReplaceAnalysesMutation,
      IWorkspaceReplaceAnalysesMutationVariables
    >(WORKSPACE_REPLACE_ANALYSES, {
      onCompleted: handleReplacedWorkspaceReport,
    });

  const handleReplaceDashboardReport = async () => {
    assertCondition(type === 'dashboard');
    const { baseId, instanceId } = replaceReport;
    const { pref } = preparePrefInput();

    await replaceAnalysisInDashboard({
      variables: {
        input: {
          targetAnalysisId,
          baseId,
          instanceId,
          newAnalysis: {
            analysisId,
            description,
            name: display,
            pref,
          },
        },
      },
    });
  };

  const handleReplaceWorkspaceReport = async () => {
    assertCondition(type === 'workspace');
    const { workspaceId } = replaceReport;
    const { pref } = preparePrefInput();
    await replaceAnalysisInWorkspace({
      variables: {
        input: {
          workspaceId,
          targetAnalysisId,
          newAnalysis: {
            analysisId,
            description,
            display,
            pref,
          },
        },
      },
    });
  };

  // only enable the button when user executed a new analysis,
  // any running analysis will also disable the button
  const isDisabled =
    analysisId === targetAnalysisId ||
    metadata.status !== AnalysisStatus.Success;

  const loading = replacingDashboardReport || replacingWorkspaceReport;
  const replaceReportContainerName =
    getReplaceReportContainerName(replaceReport);
  const popupContent = isDisabled
    ? `Re-execute the report to enable the replace action`
    : `Replace report in ${replaceReportContainerName}`;

  return (
    <KubitTooltip content={popupContent} placement="bottom-end">
      <SecondaryButton
        onClick={
          type === 'dashboard'
            ? handleReplaceDashboardReport
            : handleReplaceWorkspaceReport
        }
        disabled={loading || isDisabled}
        isOutlined={true}
        trackContext="reports"
        trackAction="replace report"
      >
        Replace
      </SecondaryButton>
    </KubitTooltip>
  );
};

export default ReplaceReportAction;
